import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { transfersService } from '../services/transfersService';
import { organizationsService, PlatformStats } from '../services/organizations.service';
import { DashboardStats, Transfer, UserRole, WeeklyTrend, StatusDistribution, MonthlyComparison } from '../types';
import { format } from 'date-fns';
import { RootState } from '../store/store';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Platform Admin Dashboard Component
const PlatformAdminDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [orgSearch, setOrgSearch] = useState('');
  const [orgFilter, setOrgFilter] = useState<'all' | 'active' | 'inactive'>('all');

  useEffect(() => {
    loadPlatformStats();
  }, []);

  const loadPlatformStats = async () => {
    try {
      setLoading(true);
      const data = await organizationsService.getPlatformStats();
      setStats(data);
    } catch (error: any) {
      toast.error(t('dashboard.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];

  const activeRate = stats && stats.totalOrganizations > 0
    ? Math.round((stats.activeOrganizations / stats.totalOrganizations) * 100)
    : 0;

  const filteredOrgs = (stats?.recentOrganizations || []).filter(org => {
    const matchSearch = org.name.toLowerCase().includes(orgSearch.toLowerCase()) ||
                        org.contactEmail.toLowerCase().includes(orgSearch.toLowerCase());
    const matchFilter = orgFilter === 'all' ||
                        (orgFilter === 'active' && org.isActive) ||
                        (orgFilter === 'inactive' && !org.isActive);
    return matchSearch && matchFilter;
  });

  const planLabel = (plan: string) => {
    const map: Record<string, string> = {
      enterprise: 'Enterprise', premium: 'Premium',
      standard: 'Standard', basic: 'Basic',
    };
    return map[plan] || plan || 'Basic';
  };

  const planBadge = (plan: string) => {
    const map: Record<string, string> = {
      enterprise: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      premium:    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      standard:   'bg-blue-100   text-blue-800   dark:bg-blue-900/30   dark:text-blue-300',
    };
    return map[plan] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            Platform Yönetimi
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Tüm organizasyonların genel durumu
          </p>
        </div>
        <button
          onClick={loadPlatformStats}
          className="self-start sm:self-auto flex items-center gap-2 px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
        >
          ↻ Yenile
        </button>
      </div>

      {/* İnaktif organizasyon uyarısı */}
      {stats && stats.inactiveOrganizations > 0 && (
        <div
          className="mb-6 flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
          onClick={() => navigate('/app/organizations')}
        >
          <span className="text-amber-500 text-xl mt-0.5">⚠️</span>
          <div>
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              {stats.inactiveOrganizations} inaktif organizasyon var
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
              Organizasyonlar sayfasından inceleyip aktive edebilir veya silebilirsiniz.
            </p>
          </div>
          <span className="ml-auto text-amber-400 text-sm">→</span>
        </div>
      )}

      {/* Stat Kartları */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
        <div
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5 cursor-pointer hover:shadow-md hover:border-blue-200 dark:hover:border-blue-700 transition-all col-span-1"
          onClick={() => navigate('/app/organizations')}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-2xl">🏢</span>
            <span className="text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-full">Toplam</span>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{stats?.totalOrganizations ?? 0}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Organizasyon</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-2xl">✅</span>
            <span className="text-xs font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2 py-0.5 rounded-full">{activeRate}%</span>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-green-600 dark:text-green-400">{stats?.activeOrganizations ?? 0}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Aktif</p>
        </div>

        <div
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4 sm:p-5 ${
            (stats?.inactiveOrganizations ?? 0) > 0
              ? 'border-amber-200 dark:border-amber-700 cursor-pointer hover:shadow-md'
              : 'border-gray-100 dark:border-gray-700'
          }`}
          onClick={() => (stats?.inactiveOrganizations ?? 0) > 0 && navigate('/app/organizations')}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-2xl">⏸️</span>
          </div>
          <p className={`text-2xl sm:text-3xl font-bold ${
            (stats?.inactiveOrganizations ?? 0) > 0
              ? 'text-amber-600 dark:text-amber-400'
              : 'text-gray-400 dark:text-gray-500'
          }`}>
            {stats?.inactiveOrganizations ?? 0}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">İnaktif</p>
        </div>

        <div
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5 cursor-pointer hover:shadow-md hover:border-purple-200 dark:hover:border-purple-700 transition-all"
          onClick={() => navigate('/app/platform/users')}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-2xl">👥</span>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-purple-600 dark:text-purple-400">{stats?.totalUsers ?? 0}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Kullanıcı</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-2xl">🚐</span>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{stats?.totalTransfers ?? 0}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Toplam Transfer</p>
        </div>
      </div>

      {/* Hızlı İşlemler */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5 mb-6">
        <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
          Hızlı İşlemler
        </h2>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate('/app/organizations/new')}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors text-sm font-medium min-h-[40px]"
          >
            <span>+</span> Yeni Organizasyon
          </button>
          <button
            onClick={() => navigate('/app/demo-email')}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 active:bg-emerald-800 transition-colors text-sm font-medium min-h-[40px]"
          >
            <span>📧</span> Demo Maili Gönder
          </button>
          <button
            onClick={() => navigate('/app/organizations')}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium min-h-[40px]"
          >
            <span>🏢</span> Tüm Organizasyonlar
          </button>
          <button
            onClick={() => navigate('/app/platform/users')}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium min-h-[40px]"
          >
            <span>👥</span> Tüm Kullanıcılar
          </button>
        </div>
      </div>

      {/* Grafikler */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Plan Dağılımı</h2>
          {(stats?.planDistribution || []).length === 0 ? (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">Veri yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={stats?.planDistribution || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ plan, count, percent }) =>
                    `${planLabel(plan)}: ${count} (%${Math.round((percent || 0) * 100)})`
                  }
                  outerRadius={75}
                  dataKey="count"
                  nameKey="plan"
                >
                  {(stats?.planDistribution || []).map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, planLabel(String(name))]} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Aylık Yeni Kayıtlar</h2>
          {(stats?.monthlyGrowth || []).length === 0 ? (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">Veri yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats?.monthlyGrowth || []} barSize={28}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#3b82f6" name="Yeni Organizasyon" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Son Organizasyonlar */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white flex-1">
              Son Organizasyonlar
            </h2>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                placeholder="İsim veya e-posta ara…"
                value={orgSearch}
                onChange={e => setOrgSearch(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[36px] w-full sm:w-52"
              />
              <select
                value={orgFilter}
                onChange={e => setOrgFilter(e.target.value as 'all' | 'active' | 'inactive')}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[36px]"
              >
                <option value="all">Tümü</option>
                <option value="active">Aktif</option>
                <option value="inactive">İnaktif</option>
              </select>
              <Link
                to="/app/organizations/new"
                className="flex items-center justify-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium min-h-[36px] whitespace-nowrap"
              >
                + Yeni
              </Link>
            </div>
          </div>
        </div>

        {filteredOrgs.length === 0 ? (
          <div className="p-8 text-center text-gray-400 dark:text-gray-500 text-sm">
            {orgSearch || orgFilter !== 'all' ? 'Arama kriterine uyan organizasyon bulunamadı.' : 'Henüz organizasyon yok.'}
          </div>
        ) : (
          <>
            {/* Desktop tablo */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Organizasyon</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">E-posta</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Durum</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Kayıt Tarihi</th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {filteredOrgs.map((org) => (
                    <tr
                      key={org.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer transition-colors"
                      onClick={() => navigate(`/app/organizations/${org.id}`)}
                    >
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900 dark:text-white">{org.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{org.contactEmail}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${planBadge(org.subscriptionPlan)}`}>
                          {planLabel(org.subscriptionPlan)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                          org.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {org.isActive ? 'Aktif' : 'İnaktif'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {format(new Date(org.createdAt), 'dd.MM.yyyy')}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-blue-500 text-sm">Düzenle →</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobil kart listesi */}
            <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-700">
              {filteredOrgs.map((org) => (
                <div
                  key={org.id}
                  className="px-4 py-4 flex items-start gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 active:bg-gray-100"
                  onClick={() => navigate(`/app/organizations/${org.id}`)}
                >
                  <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                      {org.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{org.name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        org.isActive
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                      }`}>
                        {org.isActive ? 'Aktif' : 'İnaktif'}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${planBadge(org.subscriptionPlan)}`}>
                        {planLabel(org.subscriptionPlan)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{org.contactEmail}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {format(new Date(org.createdAt), 'dd.MM.yyyy')}
                    </p>
                  </div>
                  <span className="text-gray-400 text-sm">›</span>
                </div>
              ))}
            </div>

            {filteredOrgs.length < (stats?.recentOrganizations || []).length && (
              <div className="px-4 sm:px-6 py-3 border-t border-gray-100 dark:border-gray-700 text-center">
                <button
                  onClick={() => navigate('/app/organizations')}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium"
                >
                  Tüm organizasyonları gör →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const todayTransfersRef = useRef<HTMLDivElement>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [todayTransfers, setTodayTransfers] = useState<Transfer[]>([]);
  const [weeklyTrends, setWeeklyTrends] = useState<WeeklyTrend[]>([]);
  const [statusDistribution, setStatusDistribution] = useState<StatusDistribution[]>([]);
  const [monthlyComparison, setMonthlyComparison] = useState<MonthlyComparison[]>([]);
  const [loading, setLoading] = useState(true);

  const isPlatformAdmin = user?.role === UserRole.PLATFORM_ADMIN;

  // Pool members go directly to their transfers page
  useEffect(() => {
    if (user?.role === UserRole.POOL_MEMBER) {
      navigate('/app/provider/transfers', { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const canManageTransfers = user?.role === UserRole.MANAGER ||
                             user?.role === UserRole.ORG_ADMIN;

  // STAFF users cannot see financial data
  const canViewFinancials = user?.role === UserRole.MANAGER ||
                            user?.role === UserRole.ORG_ADMIN;

  useEffect(() => {
    if (isPlatformAdmin) return;
    if (user?.role === UserRole.POOL_MEMBER) return;
    loadDashboardData();
  }, [isPlatformAdmin, user?.role]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const today = new Date().toISOString().split('T')[0];

      const [statsData, transfersData, weeklyData, statusData, monthlyData] = await Promise.all([
        transfersService.getStats(),
        transfersService.getAll({ startDate: today, endDate: today, limit: '50' }),
        transfersService.getWeeklyTrends(),
        transfersService.getStatusDistribution(),
        transfersService.getMonthlyComparison(),
      ]);

      setStats(statsData);
      setTodayTransfers([...transfersData.data].sort((a, b) => a.transferTime.localeCompare(b.transferTime)));
      setWeeklyTrends(weeklyData);
      setStatusDistribution(statusData);
      setMonthlyComparison(monthlyData);
    } catch (error: any) {
      toast.error(t('dashboard.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  // Platform Admin gets a completely different dashboard
  if (isPlatformAdmin) {
    return <PlatformAdminDashboard />;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-lg text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{t('dashboard.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm sm:text-base">{t('dashboard.subtitle')}</p>
      </div>

      {/* Stats Cards */}
      <div className={`grid grid-cols-2 gap-3 sm:gap-6 mb-8 ${canViewFinancials ? 'lg:grid-cols-4' : 'lg:grid-cols-2'}`}>
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => todayTransfersRef.current?.scrollIntoView({ behavior: 'smooth' })}
        >
          <h3 className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">{t('dashboard.todaysTransfers')}</h3>
          <p className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-400 mt-1 sm:mt-2">{stats?.todayTransfers || 0}</p>
        </div>

        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/app/transfers')}
        >
          <h3 className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">{t('dashboard.totalTransfers')}</h3>
          <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mt-1 sm:mt-2">{stats?.totalTransfers || 0}</p>
        </div>

        {canViewFinancials && (
          <>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">{t('dashboard.totalRevenue')}</h3>
              <p className="text-xl sm:text-3xl font-bold text-green-600 dark:text-green-400 mt-1 sm:mt-2">
                {stats?.totalRevenue
                  ? (typeof stats.totalRevenue === 'number'
                      ? stats.totalRevenue.toFixed(2)
                      : parseFloat(stats.totalRevenue as any).toFixed(2))
                  : '0.00'} <span className="text-sm sm:text-base">TL</span>
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
              <h3 className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">{t('dashboard.totalProfit')}</h3>
              <p className="text-xl sm:text-3xl font-bold text-green-700 dark:text-green-400 mt-1 sm:mt-2">
                {stats?.totalProfit
                  ? (typeof stats.totalProfit === 'number'
                      ? stats.totalProfit.toFixed(2)
                      : parseFloat(stats.totalProfit as any).toFixed(2))
                  : '0.00'} <span className="text-sm sm:text-base">TL</span>
              </p>
            </div>
          </>
        )}
      </div>

      {/* Today's Transfers - Moved here right after stats cards */}
      <div ref={todayTransfersRef} className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">{t('dashboard.todaysTransfers')}</h2>
          {canManageTransfers && (
            <Link
              to="/app/transfers/new"
              className="bg-blue-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg hover:bg-blue-700 text-xs sm:text-sm font-medium"
            >
              + <span className="hidden sm:inline">{t('dashboard.newTransfer')}</span><span className="sm:hidden">{t('dashboard.new')}</span>
            </Link>
          )}
        </div>

        {todayTransfers.length === 0 ? (
          <div className="p-6 sm:p-8 text-center text-gray-500 dark:text-gray-400">
            <p>{t('dashboard.noTransfersToday')}</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
              {todayTransfers.map((transfer) => (
                <div key={transfer.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{transfer.customerName}</p>
                      <p className="text-sm text-gray-500">{transfer.customerPhone}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(transfer.status)}`}>
                      {t(`transfers.statuses.${transfer.status}`)}
                    </span>
                  </div>
                  <div className="text-sm mb-2">
                    <span className="text-gray-600 dark:text-gray-400">
                      {transfer.transferTime.substring(0, 5)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-900 dark:text-gray-100 mb-2">
                    {transfer.pickupLocation}
                    <span className="text-gray-500 mx-1">→</span>
                    {transfer.dropoffLocation}
                  </div>
                  {canViewFinancials && (
                    <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                      {transfer.providerDriverName && (
                        <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-gray-700 dark:text-gray-300">
                          {transfer.providerDriverName}
                        </span>
                      )}
                      <span className="text-sm font-medium text-green-600">{transfer.customerPrice} TL</span>
                      {transfer.providerPrice && (
                        <span className="text-sm text-gray-500">/ {transfer.providerPrice} TL</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('dashboard.customer')}
                    </th>
                    {!canViewFinancials && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('dashboard.transferType')}
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('dashboard.dateTime')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('dashboard.route')}
                    </th>
                    {!canViewFinancials && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('dashboard.flightCode')}
                      </th>
                    )}
                    {canViewFinancials && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('dashboard.assignedTo')}
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.status')}
                    </th>
                    {canViewFinancials && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('dashboard.customerPrice')}
                      </th>
                    )}
                    {canViewFinancials && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('dashboard.providerPrice')}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {todayTransfers.map((transfer) => (
                    <tr key={transfer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 text-sm">
                        <div className="font-medium text-gray-900">{transfer.customerName}</div>
                        <div className="text-gray-500">{transfer.customerPhone}</div>
                      </td>
                      {!canViewFinancials && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {t(`transfers.${transfer.tripType}`)}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div>{format(new Date(transfer.transferDate), 'dd.MM.yyyy')}</div>
                        <div className="text-gray-500">{transfer.transferTime.substring(0, 5)}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div>{transfer.pickupLocation}</div>
                        <div className="text-gray-500">→ {transfer.dropoffLocation}</div>
                      </td>
                      {!canViewFinancials && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transfer.flightCode || '-'}
                        </td>
                      )}
                      {canViewFinancials && (
                        <td className="px-6 py-4 text-sm">
                          {transfer.providerDriverName ? (
                            <>
                              <div className="font-medium text-gray-900">{transfer.providerDriverName}</div>
                              {transfer.providerDriverPhone && (
                                <div className="text-gray-500">{transfer.providerDriverPhone}</div>
                              )}
                            </>
                          ) : (
                            <div className="text-gray-900">
                              {transfer.assignedDriver?.fullName ||
                               transfer.assignedProvider?.name ||
                               transfer.assignedOrganization?.name ||
                               '-'}
                            </div>
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                            transfer.status
                          )}`}
                        >
                          {t(`transfers.statuses.${transfer.status}`)}
                        </span>
                      </td>
                      {canViewFinancials && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {transfer.customerPrice} TL
                        </td>
                      )}
                      {canViewFinancials && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {transfer.providerPrice ? `${transfer.providerPrice} TL` : '-'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Analytics Charts */}
      <div className={`grid grid-cols-1 gap-4 sm:gap-6 mb-8 ${canViewFinancials ? 'lg:grid-cols-2' : 'lg:grid-cols-3'}`}>
        {/* Weekly Trends */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">{t('dashboard.weeklyTrends')}</h2>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={weeklyTrends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              {canViewFinancials ? (
                <>
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                </>
              ) : (
                <YAxis />
              )}
              <Tooltip />
              <Legend />
              {canViewFinancials ? (
                <>
                  <Line yAxisId="left" type="monotone" dataKey="transfers" stroke="#3b82f6" name={t('dashboard.transfers')} />
                  <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#10b981" name={t('dashboard.revenue')} />
                </>
              ) : (
                <Line type="monotone" dataKey="transfers" stroke="#3b82f6" name={t('dashboard.transfers')} strokeWidth={2} />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Status Distribution */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">{t('dashboard.statusDistribution')}</h2>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={statusDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({status, count}) => `${status}: ${count}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="count"
              >
                {statusDistribution.map((entry, index) => {
                  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];
                  return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                })}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

      </div>

      {/* Monthly Comparison */}
      {canViewFinancials && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6 mb-8">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">{t('dashboard.monthlyComparison')}</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={monthlyComparison}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="revenue" fill="#10b981" name={t('dashboard.revenue')} />
              <Bar dataKey="cost" fill="#ef4444" name={t('dashboard.cost')} />
              <Bar dataKey="profit" fill="#3b82f6" name={t('dashboard.profit')} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-purple-100 text-purple-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}
